import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { ChatChannelKind, ChatChannelSummary, ChatMessage, DmTarget } from '@pk/shared';
import { api } from '../api/client';
import { qk, useChatBlocks, useChatChannels, useChatMessages, useDmTargets, useMe } from '../api/hooks';
import { Badge, Btn, Empty, ErrorBox, Field, Loading, PageHeader, Panel, Screen, ScreenTop, ScrollArea, Section, Sheet } from '../components/ui';
import { IconChat, IconChevron } from '../components/icons';
import { useUi } from '../store';
import '../styles/chat.css';

const MAX_LEN = 500;

/** Time only for today, otherwise a short date (+ time unless `dateOnly`). Keeps rows and bubbles narrow on phones. */
function fmtWhen(ms: number, dateOnly = false): string {
  const d = new Date(ms);
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (d.toDateString() === new Date().toDateString()) return time;
  const date = d.toLocaleDateString([], { day: 'numeric', month: 'short' });
  return dateOnly ? date : `${date}, ${time}`;
}
/** UX-only preview of the server's slow mode (the server still enforces it with 429). */
const SLOW_MODE_MS: Partial<Record<ChatChannelKind, number>> = { global: 15_000, newcomers: 5_000 };

/** Same id scheme as the server: the account pair, sorted, joined. */
export function dmChannelId(a: string, b: string): string {
  return `dm:${[a, b].sort().join(':')}`;
}

export function ChatScreen() {
  const { t } = useTranslation();
  const channels = useChatChannels();
  const me = useMe();
  const [params, setParams] = useSearchParams();
  const channelId = params.get('channel');

  if (channels.isLoading) return <Loading />;
  if (channels.isError || !channels.data) {
    // The server 403s GET /api/chat while the account is banned and carries bannedUntilMs in the
    // body (attached to the thrown error): show the read-only kicked state instead of a raw error.
    const err = channels.error as (Error & { status?: number; bannedUntilMs?: number | null }) | null;
    const banMs = typeof err?.bannedUntilMs === 'number' ? err.bannedUntilMs : null;
    if (err && err.status === 403 && banMs !== null) {
      const permanent = banMs >= Number.MAX_SAFE_INTEGER - 1;
      return (
        <Screen className="chat-page">
          <ScreenTop><PageHeader icon={<IconChat />} title={t('chat.title')} subtitle={t('chat.sub')} /></ScreenTop>
          <ScrollArea><Panel>
            <div className="chat-banner danger">
              {permanent ? t('chat.bannedPermanent') : t('chat.banned', { until: new Date(banMs).toLocaleString() })}
            </div>
            <p className="muted">{t('chat.bannedKicked')}</p>
            <Btn small onClick={() => { void channels.refetch(); }}>{t('chat.retry')}</Btn>
          </Panel></ScrollArea>
        </Screen>
      );
    }
    return <ErrorBox error={channels.error} onRetry={() => { void channels.refetch(); }} />;
  }
  const chs = channels.data.channels;
  const mod = channels.data;

  const select = (id: string) => setParams({ channel: id }, { replace: false });
  const back = () => setParams({}, { replace: true });
  const accountId = me.data?.accountId ?? '';

  return (
    <Screen className={`chat-page ${channelId ? 'in-channel' : ''}`}>
      {!channelId && <ScreenTop><PageHeader icon={<IconChat />} title={t('chat.title')} subtitle={t('chat.sub')} /></ScreenTop>}
      {channelId ? (
        <ChannelView
          channelId={channelId}
          channel={chs.find((c) => c.id === channelId) ?? null}
          mutedUntilMs={mod.mutedUntilMs}
          bannedUntilMs={mod.bannedUntilMs}
          accountId={accountId}
          onBack={back}
        />
      ) : (
        <ChannelList channels={chs} onPick={select} />
      )}
    </Screen>
  );
}

// ---------------------------------------------------------------- channel list

const GROUPS: { key: 'general' | 'realm' | 'alliances' | 'dm'; kinds: ChatChannelKind[] }[] = [
  { key: 'general', kinds: ['global', 'newcomers'] },
  { key: 'realm', kinds: ['realm'] },
  { key: 'alliances', kinds: ['alliance'] },
  { key: 'dm', kinds: ['dm'] },
];

function ChannelList({ channels, onPick }: { channels: ChatChannelSummary[]; onPick: (id: string) => void }) {
  const { t } = useTranslation();
  const me = useMe();
  const targets = useDmTargets();
  const [dmOpen, setDmOpen] = useState(false);
  const [pick, setPick] = useSearchParams();

  const startDm = (acc: string) => {
    setDmOpen(false);
    pick.set('channel', dmChannelId(me.data?.accountId ?? '', acc));
    setPick(pick, { replace: false });
  };

  return (
    <ScrollArea>
      {channels.length === 0 && <Panel><Empty>{t('chat.empty')}</Empty></Panel>}
      {GROUPS.map(({ key, kinds }) => {
        const items = channels.filter((c) => kinds.includes(c.kind));
        if (key === 'dm') {
          return (
            <Section key={key} title={t(`chat.groups.${key}`)} right={
              <Btn small variant="quiet" onClick={() => setDmOpen(true)}>{t('chat.dm.new')}</Btn>
            }>
              {items.length > 0 ? (
                <Panel><div className="chat-channels">{items.map((c) => <ChannelRow key={c.id} c={c} onPick={onPick} />)}</div></Panel>
              ) : (
                <Panel><Empty>{t('chat.dm.empty')}</Empty></Panel>
              )}
            </Section>
          );
        }
        if (items.length === 0) return null;
        return (
          <Section key={key} title={t(`chat.groups.${key}`)}>
            <Panel><div className="chat-channels">{items.map((c) => <ChannelRow key={c.id} c={c} onPick={onPick} />)}</div></Panel>
          </Section>
        );
      })}
      <Sheet open={dmOpen} onClose={() => setDmOpen(false)} title={t('chat.dm.pick')}>
        <DmPicker targets={targets.data?.targets ?? []} loading={targets.isLoading} onPick={startDm} />
      </Sheet>
    </ScrollArea>
  );
}

function ChannelRow({ c, onPick }: { c: ChatChannelSummary; onPick: (id: string) => void }) {
  const { t } = useTranslation();
  return (
    <button type="button" className={`chat-channel ${c.unread > 0 ? 'unread' : ''}`} onClick={() => onPick(c.id)}>
      <span className="chat-channel-name">{c.name}</span>
      <span className="chat-channel-right">
        {c.lastMessageAtMs !== null && <span className="chat-channel-time muted small">{fmtWhen(c.lastMessageAtMs, true)}</span>}
        {c.unread > 0 ? <Badge tone="gold" solid>{t('chat.unread', { count: c.unread })}</Badge> : null}
      </span>
    </button>
  );
}

function DmPicker({ targets, loading, onPick }: { targets: DmTarget[]; loading: boolean; onPick: (accountId: string) => void }) {
  const { t } = useTranslation();
  if (loading) return <Loading />;
  if (targets.length === 0) return <Empty>{t('chat.dm.empty')}</Empty>;
  return (
    <div className="chat-channels">
      {targets.map((x) => (
        <button type="button" key={x.accountId} className="chat-channel" onClick={() => onPick(x.accountId)}>
          <span className="chat-channel-name">{x.houseName}</span>
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- channel view

function ChannelView({ channelId, channel, mutedUntilMs, bannedUntilMs, accountId, onBack }: {
  channelId: string;
  channel: ChatChannelSummary | null;
  mutedUntilMs: number | null;
  bannedUntilMs: number | null;
  accountId: string;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const messages = useChatMessages(channelId);
  const blocks = useChatBlocks();
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [report, setReport] = useState<{ kind: 'message' | 'user'; messageId?: string; accountId?: string; name: string } | null>(null);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [, tick] = useState(0);
  const markedRef = useRef<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Ticking clock only while a local slow-mode countdown runs (UX-only; the server enforces it too).
  useEffect(() => {
    if (cooldownUntil <= Date.now()) return;
    const iv = setInterval(() => tick((x) => x + 1), 500);
    return () => clearInterval(iv);
  }, [cooldownUntil, text]);

  // Oldest→newest across all loaded pages (each page arrives newest-first).
  const all = useMemo(() => {
    const pages = messages.data?.pages ?? [];
    return pages.flatMap((p) => p.messages).reverse();
  }, [messages.data]);

  const blockedIds = useMemo(() => new Set((blocks.data?.blocked ?? []).map((b) => b.accountId)), [blocks.data]);
  const visible = all.filter((m) => !blockedIds.has(m.senderAccountId));
  const hiddenCount = all.length - visible.length;

  // Mark read on select and whenever a new message lands (incl. via the ws 'chat' push).
  const newestId = all[all.length - 1]?.id ?? null;
  useEffect(() => {
    if (!channelId || !newestId || markedRef.current === newestId) return;
    markedRef.current = newestId;
    void api.readChat(channelId)
      .then(() => qc.invalidateQueries({ queryKey: qk.chat }))
      .catch(() => undefined);
  }, [channelId, newestId, qc]);

  // Stick to the newest message when one lands (or the first page arrives).
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [newestId, channelId, messages.data !== undefined]);

  // Grow the composer with its text (up to ~4 lines).
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight + 2, 120)}px`;
  }, [text]);

  const kind = channel?.kind ?? classify(channelId);
  const mutedLive = mutedUntilMs !== null && mutedUntilMs > Date.now();
  const bannedLive = bannedUntilMs !== null && bannedUntilMs > Date.now();
  const permanent = (ms: number | null) => ms !== null && ms >= Number.MAX_SAFE_INTEGER - 1;
  const waitMs = SLOW_MODE_MS[kind] ? cooldownUntil - Date.now() : 0;
  const canSend = !mutedLive && !bannedLive && waitMs <= 0;
  const canLoadOlder = !!messages.data?.pages[0]?.hasMore;

  const block = useMutation({
    mutationFn: (id: string) => api.blockUser(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.chatBlocks });
      void qc.invalidateQueries({ queryKey: qk.dmTargets });
      useUi.getState().pushToast('info', t('chat.block.done'));
    },
  });
  const unblock = useMutation({
    mutationFn: (id: string) => api.unblockUser(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.chatBlocks });
      void qc.invalidateQueries({ queryKey: qk.dmTargets });
      useUi.getState().pushToast('info', t('chat.block.unblocked'));
    },
  });

  const send = async () => {
    const body = text.trim();
    if (!body || !canSend) return;
    setSending(true);
    setSendError(null);
    try {
      await api.sendChatMessage(channelId, body);
      setText('');
      void qc.invalidateQueries({ queryKey: qk.chat });
      void qc.invalidateQueries({ queryKey: qk.chatMessages(channelId) });
      if (SLOW_MODE_MS[kind]) setCooldownUntil(Date.now() + SLOW_MODE_MS[kind]!);
    } catch (e) {
      const msg = e instanceof Error && e.message !== 'network' ? e.message : t('error.network');
      setSendError(msg);
      useUi.getState().pushToast('error', msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className="chat-conv">
        <header className="chat-conv-head">
          <button type="button" className="chat-back" onClick={onBack} aria-label={t('chat.back')}><IconChevron /></button>
          <h2>{channel?.name ?? channelId}</h2>
        </header>
        {bannedLive && (
          <div className="chat-banner danger">{permanent(bannedUntilMs) ? t('chat.bannedPermanent') : t('chat.banned', { until: new Date(bannedUntilMs!).toLocaleString() })}</div>
        )}
        {mutedLive && (
          <div className="chat-banner">{permanent(mutedUntilMs) ? t('chat.mutedPermanent') : t('chat.muted', { until: new Date(mutedUntilMs!).toLocaleString() })}</div>
        )}

        <div className="chat-scroll" ref={scrollRef}>
          {messages.isLoading && <Loading />}
          {messages.isError && <ErrorBox error={messages.error} onRetry={() => { void messages.refetch(); }} />}
          {messages.data && (
            <>
              <div className="chat-load-older">
                {canLoadOlder
                  ? <Btn small variant="quiet" busy={messages.isFetchingPreviousPage} onClick={() => { void messages.fetchPreviousPage(); }}>{t('chat.loadOlder')}</Btn>
                  : <span className="muted small">{t('chat.noOlder')}</span>}
              </div>
              {visible.length === 0 && <Empty>{t('chat.emptyChannel')}</Empty>}
              <div className="chat-messages">
                {visible.map((m) => (
                  <MessageRow key={m.id} m={m} mine={m.senderAccountId === accountId} onReport={setReport} onBlock={(id) => block.mutate(id)} />
                ))}
              </div>
              {hiddenCount > 0 && <p className="chat-hidden muted small">{t('chat.hidden', { count: hiddenCount })}</p>}
              {(blocks.data?.blocked ?? []).length > 0 && (
                <p className="chat-blocks muted small">
                  {(blocks.data?.blocked ?? []).map((b) => (
                    <button key={b.accountId} type="button" className="link small" onClick={() => unblock.mutate(b.accountId)}>
                      {t('chat.block.unblock')} {b.houseName}
                    </button>
                  ))}
                </p>
              )}
            </>
          )}
        </div>

        <div className="chat-composer">
          {waitMs > 0 && <p className="muted small chat-note">{t('chat.slow', { s: Math.ceil(waitMs / 1000) })}</p>}
          {sendError && <p className="chat-warn chat-note">{sendError}</p>}
          <div className="chat-composer-row">
            <textarea
              ref={inputRef}
              rows={1}
              enterKeyHint="send"
              className="input chat-input"
              value={text}
              maxLength={MAX_LEN}
              disabled={mutedLive || bannedLive}
              placeholder={t('chat.composer.placeholder')}
              aria-label={t('chat.composer.placeholder')}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); }
              }}
            />
            {/* keep the keyboard up when tapping Send */}
            <div className="chat-send" onMouseDown={(e) => e.preventDefault()}>
              <Btn variant="primary" disabled={!canSend || text.trim().length === 0} busy={sending} onClick={() => { void send(); }}>{t('chat.composer.send')}</Btn>
            </div>
          </div>
          <div className="chat-composer-meta">
            <span className={`muted small ${text.length > MAX_LEN - 80 ? 'chat-count-show' : 'chat-count'} ${text.length > MAX_LEN - 20 ? 'chat-warn' : ''}`}>{t('chat.composer.count', { n: text.length, max: MAX_LEN })}</span>
            <span className="muted small chat-hint">{t('chat.composer.hint')}</span>
          </div>
        </div>
      </div>

      <ReportSheet report={report} onClose={() => setReport(null)} />
    </>
  );
}

/** Server channel ids are self-describing enough for the mock-less case where the list has not loaded. */
function classify(channelId: string): ChatChannelKind {
  if (channelId === 'global' || channelId === 'newcomers') return channelId;
  if (channelId.startsWith('dm:')) return 'dm';
  if (channelId.startsWith('alliance:')) return 'alliance';
  return 'realm';
}

function MessageRow({ m, mine, onReport, onBlock }: {
  m: ChatMessage;
  mine: boolean;
  onReport: (r: { kind: 'message' | 'user'; messageId?: string; accountId?: string; name: string }) => void;
  onBlock: (accountId: string) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const canAct = !mine && !m.deleted;
  return (
    <div className={`chat-message ${mine ? 'mine' : ''}`}>
      <div className="chat-bubble">
        <div className="chat-message-head">
          <b>{m.senderName}</b>
          <span className="muted small">{fmtWhen(m.sentAtMs)}</span>
          {canAct && (
            <button type="button" className="chat-more" aria-expanded={open} aria-label={t('ui.details')} onClick={() => setOpen((v) => !v)}>
              <span aria-hidden="true">{'\u22EF'}</span>
            </button>
          )}
        </div>
        {m.deleted
          ? <p className="chat-message-text muted chat-removed">{t('chat.removed')}</p>
          : <p className="chat-message-text">{m.text}</p>}
      </div>
      {canAct && open && (
        <div className="chat-message-actions">
          <button type="button" className="chat-act" onClick={() => onReport({ kind: 'message', messageId: m.id, accountId: m.senderAccountId, name: m.senderName })}>{t('chat.report.message')}</button>
          <button type="button" className="chat-act" onClick={() => onBlock(m.senderAccountId)}>{t('chat.block.action')}</button>
          <button type="button" className="chat-act" onClick={() => onReport({ kind: 'user', accountId: m.senderAccountId, name: m.senderName })}>{t('chat.report.user')}</button>
        </div>
      )}
    </div>
  );
}

function ReportSheet({ report, onClose }: { report: { kind: 'message' | 'user'; messageId?: string; accountId?: string; name: string } | null; onClose: () => void }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const reportMutation = useMutation({
    mutationFn: () => api.reportChat({
      kind: report!.kind,
      messageId: report!.messageId,
      accountId: report!.accountId,
      reason: reason.trim(),
    }),
    onSuccess: () => {
      useUi.getState().pushToast('info', t('chat.report.success'));
      setReason('');
      onClose();
    },
  });
  const body = reason.trim();
  return (
    <Sheet open={!!report} onClose={onClose} title={report?.kind === 'message' ? t('chat.report.message') : t('chat.report.user')}>
      {report && (
        <div>
          <p className="muted">{report.name} — {t('chat.report.help')}</p>
          <Field label={t('chat.report.reason')}>
            <textarea className="input" maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('chat.report.reasonPh')} />
          </Field>
          {reportMutation.isError && <p className="chat-warn">{(reportMutation.error as Error)?.message ?? t('error.generic')}</p>}
          <Btn variant="primary" block disabled={body.length === 0} busy={reportMutation.isPending}
            onClick={() => reportMutation.mutate()}>
            {t('chat.report.submit')}
          </Btn>
        </div>
      )}
    </Sheet>
  );
}